<template>
  <div class="calendar-wrapper">
    <div v-if="isNeedSmallCalendar || isNeedTags" class="calendar-left-block">
      <div class="calendar-header">
        <i class="calendar-header-icon bx bx-calendar-alt">
          <span class="calendar-header-icon-day">{{ new Date().getDate() }}</span>
        </i>Календарь
      </div>

      <SmallCalendar
        v-if="isNeedSmallCalendar"
        :fullMonthName="fullMonthName"
        :currentMonth="currentMonth"
        :currentYear="currentYear"
        :dayNames="dayNames"
        :filtredTasksByTags="filtredTasksByTags"
        :LastDaysPrevMonth="LastDaysPrevMonth()"
        :FirstDaysNextMonth="FirstDaysNextMonth()"
        @openWeekWithDay="openWeekWithDay"
        @prevMonth="prevMonth()"
        @nextMonth="nextMonth()"
      />

      <div class="calendar-select-user-wrapper">
        <tsoft-select-user
          id="user-calendar"
          :roles="`sales-manager||sales-head-manager||logistics-manager||logistics-head-manager||logistics-multim-manager||logistics-multim-head-manager||TO-manager||TO-head-manager||bsz-manager||b2c-head-manager||b2c-manager||lawyer-manager||lawyer-head-manager||hr-manager||hr-head-manager`"
          :needNullElement="true"
          @set="setUserId"
          ></tsoft-select-user>
      </div>

      <TasksTags
        v-if="isNeedTags"
        :tags="tags"
        @activeTag="activeTag"
      />
    </div>

    <div class="calendar-main">
      <div class="calendar-main-header pl-2">
        <button 
          class="calendar-main-button-today"
          @click="goToToday"
        >Сегодня</button>

        <button 
          @click="prevMonth" 
          class="calendar-main-arrow mr-2"
        ><i class="bx bx-chevron-left"></i></button>

        <p class="calendar-main-month-name mr-2">{{ fullMonthName }}</p>
        <p class="calendar-main-year mr-2">{{ currentYear }}</p>

        <button 
          @click="nextMonth"
          class="calendar-main-arrow mr-4"
        ><i class="bx bx-chevron-right"></i></button>

        <button 
          @click="prevWeek" 
          class="calendar-main-arrow mr-2"
        ><i class="bx bx-chevron-left"></i></button>
        <p class="calendar-main-year mr-2">Неделя {{ currentWeek }}</p>
        <button 
          @click="nextWeek"
          class="calendar-main-arrow mr-4"
        ><i class="bx bx-chevron-right"></i></button>

        <b-button
          @click="refreshDataInTasks()"
          size="sm"
          variant="reload"
          class="mr-4"
        >
          <b-icon
            icon="arrow-repeat"
            scale="1.5"
            aria-label="Reload"
            :animation="isTaskRefreshing ? 'spin' : ''"
          ></b-icon>
        </b-button>

        <button 
          class="calendar-main-button-today"
          @click="weekIsShow = !weekIsShow"
        >{{ weekIsShow ? 'Показать месяц' : 'Показать неделю' }}</button>
      </div>

      <div 
        :class="['calendar-main-wrapper', weekIsShow ? 'calendar-main-wrapper-week' : 'calendar-main-wrapper-month']"
      >
        <div v-if="weekIsShow" class="calendar-main-time-points">
          <p class="calendar-main-time-point"></p>
          <p v-for="time in times" :key="time.time" class="calendar-main-time-block">{{ time.time }}</p>
        </div>

        <div class="calendar-days-wrapper">
          <div class="calendar-main-weekdays">
            <div v-for="(dayName, dayIndex) in dayNames"
                :key="dayIndex"
                :class="['calendar-main-weekday', weekIsShow ? 'calendar-main-weekday-week' : '']">
              {{ dayName }}
            </div>
          </div>

          <div v-if="!weekIsShow" :class="['calendar-main-days', {'calendar-main-days-full': countOfDays > 35 }]">
            <div v-for="day in LastDaysPrevMonth()"
              :key="100 + day" class="calendar-main-start-day"
            > 
              {{ day }}
            </div>

            <div
              v-for="currentDay in moment(`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`).daysInMonth()"
              :key="currentDay"
              :class="['calendar-main-day', isToday(currentDay) ? 'calendar-main-current-day' : '']"
              @click="openWeekWithDay(currentDay, $event)"
            >
              <p class="mb-1">{{ currentDay }}</p>
              <div 
                v-for="task in filterTaskByDay(currentDay)"
                :key="task.id"
                class="calendar-main-time-block-task-wrapper mb-1"
                :class="handlerStyleTags(task)"
                @click="changeTask(task)"
              >
                <span class="mr-1">{{ task.id }}</span><span class="task-wrapper-text">{{ task.title }}</span>
                <span class="calendar-task-small-icon" title="Задача завершена" v-if="task.reference_status_id == 403">✓</span>                
                <span class="calendar-task-small-icon" title="Задача на проверке" v-if="task.reference_status_id == 402">👁</span>
                <span class="calendar-task-small-icon" title="Задача в работе" v-if="task.reference_status_id == 401">✐</span>
              </div>
              <div class="calendar-sum-of-tasks-on-day" v-if="sumOfTaskByDay(currentDay) > 0"><b-badge variant="secondary" title="Количество задач запланированных на день">{{ sumOfTaskByDay(currentDay) }}</b-badge></div>
            </div>

            <div v-for="day in FirstDaysNextMonth()"
              :key="200 + day" class="calendar-main-start-day"
            >{{ day }}</div>
          </div>

          <div v-if="weekIsShow">
            <div class="calendar-main-week-days">
              <div
                v-for="currentDay in allTasksInWeek"
                :key="currentDay.day"
                :class="['calendar-main-week-day', isToday(currentDay.day) ? 'calendar-main-current-day' : '']"
              >
                <p class="calendar-main-day-number">{{ currentDay.day }}</p>
                <TimeWrapper
                  v-for="time in currentDay.times"
                  :key="time.time"
                  :time="time"
                  :currentDay="currentDay"
                  @setDateToTask="setDateToTask"
                  @changeTask="changeTask"
                  @moved="moved"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CreateTaskWidget
      :realizationData="taskData.realization_at"
      @setNewCreatedTask="refreshDataInTasks"
      @openCreateTaskSidebar="openCreateTaskSidebar"
    />
  </div>
</template>

<script>
import SmallCalendar from '@/components/calendar/SmallCalendar';
import TasksTags from '@/components/calendar/TasksTags';
import TimeWrapper from '@/components/calendar/TimeWrapper';
import CreateTaskWidget from '@/components/task/CreateTaskWidget.vue';
import times from '@/constants/times';
import { API_TASKS } from "@/constants";
import { mapGetters } from "vuex";
import { openTaskSidebar } from '@/mixins/openTaskSidebar';
import { openCreateTaskSidebar } from '@/mixins/openTasksSidebar';
import { defaultDataItems } from '@/mixins';

export default {
  name: 'Calendar',

  mixins: [
    openTaskSidebar,
    openCreateTaskSidebar,
    defaultDataItems,
  ],

  props: {
    elementIds: {
      type: Array,
      default: () => []
    }, // Array of elements ids for creating mass tasks
    elementDBField: { type: String|Number, default: ''}, // For creating mass task we need to know what type are them: order_id, company_id, finance_id
    isNeedSmallCalendar: {
      type: Boolean, 
      default: true,
    },
    isNeedTags: {
      type: Boolean, 
      default: true,
    },
  },

  components: {
    SmallCalendar,
    TasksTags,
    TimeWrapper,
    CreateTaskWidget,
},

  data() {
    return {
      value: '',
      dayNames: [
        'Пн',
        'Вт',
        'Ср',
        'Чт',
        'Пт',
        'Сб',
        'Вс',
      ],
      times: times,
      time: null,
      currentDay: this.$moment().format('D'),
      currentWeek: this.$moment(this.$moment().toDate(), 'MM-DD-YYYY').isoWeek(),
      currentMonth: this.$moment().month(),
      currentYear: this.$moment().year(),
      weekIsShow: false,
      allTasks: [],
      taskText: '',
      isTaskRefreshing: false,
      taskData: {
        order_id: '',
        company_id: '',
        finance_id: '',
        order_service_id: '',
        title: '',
        description: '',
        realization_at: '',
        reference_type_id: '406',
        realizators: [],
      },
      options: [
        { value: '405', text: 'Связаться', title: 'Необходимо связаться с клиентом' },
        { value: '404', text: 'Встреча', title: 'Встреча с клиентом' },
        { value: '547', text: 'Узнать ответ на КП', title: 'Узнать ответ на КП' },
        { value: '548', text: 'Договор', title: 'Получить обратную связь по договору' },
        { value: '406', text: 'Другое' },
      ],
      selectedPerformers: [],
      tags: {
        'order': {name: 'Заказ', active: true, id: 'order', class: 'tags-style-bg-order_id'},
        'company': {name: 'Компания', active: true, id: 'company', class: 'tags-style-bg-company_id'},
        'finance': {name: 'Финансы', active: true, id: 'finance', class: 'tags-style-bg-finance_id'},
        'order_service': {name: 'Услуга', active: true, id: 'order_service', class: 'tags-style-bg-order_service_id'},
        'notification': {name: 'Напоминание', active: true, id: 'notification', class: 'tags-style-bg-notification'},
      },
      calendarUserId: null,
    }
  },

  watch: {
    'currentWeek'() {
      this.currentWeek = String(this.currentWeek).padStart(2, '0');
    },

    'currentMonth'() {
      this.refreshDataInTasks();
    },

    '$route.query.task': {
      handler() {
        if (!this.$route.query.task) {
          this.refreshDataInTasks();
        }
      },
    },

    'calendarUserId'() {
      this.getTaskList();
    },
  },

  computed: {
    fullMonthName() {
      return new Date(this.currentYear, this.currentMonth).toLocaleString('ru-RU', {
            month: 'long',
          })
    },
    ...mapGetters("auth", ["currentUser"]),
    compAllTasks() {
      return this.allTasks;
    },
    filtredTasksByTags() {
      return this.compAllTasks.filter(task => {
        return (task.order_id && this.tags.order.active) ||
        (task.finance_id && this.tags.finance.active) ||
        (task.company_id && this.tags.company.active) ||
        (task.order_service_id && this.tags.order_service.active) ||
        (this.tags.notification.active && !task.order_id && !task.finance_id && !task.company_id && !task.order_service_id)
      })
    },
    countOfDays() {
      return this.$moment(`${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}`).daysInMonth() + this.FirstDaysNextMonth().length + this.LastDaysPrevMonth().length
    },
    allTasksInWeek() {
      let allTasks = [];

      this.daysInWeek().forEach((day, index) => {
        if (this.LastDaysPrevMonth().length && (+this.LastDaysPrevMonth()[0] === +this.daysInWeek()[0])) {
          if (index < (this.LastDaysPrevMonth().length)) {
            let month = this.currentMonth;
            if (month === 0) {
              month = 12;
            }
            allTasks.push({
              day: day,
              month: month,
              year: this.currentYear,
              times: JSON.parse(JSON.stringify(this.times)),
            })
          } else {
            const month = this.currentMonth + 1;
            allTasks.push({
              day: day,
              month: month,
              year: this.currentYear,
              times: JSON.parse(JSON.stringify(this.times)),
            })
          }
        } else if (this.FirstDaysNextMonth().length && (+this.FirstDaysNextMonth()[this.FirstDaysNextMonth().length - 1] === +this.daysInWeek()[6])) {
          if (index >= (this.daysInWeek().length - this.FirstDaysNextMonth().length)) {
            let month = this.currentMonth + 2;
            if (month === 13) {
              month = 1
            }
            allTasks.push({
              day: day,
              month: month,
              year: this.currentYear,
              times: JSON.parse(JSON.stringify(this.times)),
            })
          } else {
            const month = this.currentMonth + 1;
            allTasks.push({
              day: day,
              month: month,
              year: this.currentYear,
              times: JSON.parse(JSON.stringify(this.times)),
            })
          }
        } else {
          const month = this.currentMonth + 1;
          allTasks.push({
            day: day,
            month: month,
            year: this.currentYear,
            times: JSON.parse(JSON.stringify(this.times)),
          })
        }
      })

      allTasks.forEach(d => d.times.forEach((t, index) => {
        t.tasks = [];
        t.id = index;
      }))

      this.filtredTasksByTags.forEach(task => { // все задачи с тэгами
        allTasks.forEach((currendDay, dayIndex) => {
          currendDay.times.forEach((t, timeIndex) => {
            let taskTimeHours = task.realization_at.split(' ')[1].split(':')[0];
            let taskTimeMinutes = task.realization_at.split(' ')[1].split(':')[1];
            let tTimeHours = t.time.split(':')[0];
            let tTimeMinutes = t.time.split(':')[1];
            let dateWithCurrentMonth = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(currendDay.day).padStart(2, '0')}`;
            let dateWithPrevMonth = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-${String(currendDay.day).padStart(2, '0')}`;
            let dateWithNextMonth = `${this.currentYear}-${String(this.currentMonth + 2).padStart(2, '0')}-${String(currendDay.day).padStart(2, '0')}`;

            if (this.currentMonth === 0) {
              dateWithPrevMonth = `${this.currentYear - 1}-12-${String(currendDay.day).padStart(2, '0')}`;
            } else if (this.currentMonth === 11) {
              dateWithNextMonth = `${this.currentYear + 1}-01-${String(currendDay.day).padStart(2, '0')}`;
            }

            if (dateWithCurrentMonth === task.realization_at.split(' ')[0] && taskTimeHours === tTimeHours) {
              let diff = +taskTimeMinutes - +tTimeMinutes
              if (diff >= 0 && diff < 30) {
                const index = allTasks.findIndex(item => `${item.year}-${String(item.month).padStart(2, '0')}-${String(item.day).padStart(2, '0')}` === dateWithCurrentMonth);
                if (index !== -1) {
                  allTasks[index].times[timeIndex].tasks.push(task);
                }
              }
            }
            if (dateWithPrevMonth === task.realization_at.split(' ')[0] && taskTimeHours === tTimeHours) {
              let diff = +taskTimeMinutes - +tTimeMinutes
              if (diff >= 0 && diff < 30) {
                const index = allTasks.findIndex(item => `${item.year}-${String(item.month).padStart(2, '0')}-${String(item.day).padStart(2, '0')}` === dateWithPrevMonth);
                if (index !== -1) {
                  allTasks[index].times[timeIndex].tasks.push(task);
                }
              }
            }
            if (dateWithNextMonth === task.realization_at.split(' ')[0] && taskTimeHours === tTimeHours) {
              let diff = +taskTimeMinutes - +tTimeMinutes
              if (diff >= 0 && diff < 30) {
                const index = allTasks.findIndex(item => `${item.year}-${String(item.month).padStart(2, '0')}-${String(item.day).padStart(2, '0')}` === dateWithNextMonth);
                if (index !== -1) {
                  allTasks[index].times[timeIndex].tasks.push(task);
                }
              }
            }
          })
        })
      })
      return allTasks
    },
  },

  mounted() {
    this.currentWeek = String(this.currentWeek).padStart(2, '0');
    this.getTaskList();
  },

  methods: {
    countOfLastDays() {
      if (true) {
        if (new Date(this.currentYear, this.currentMonth, 1).getDay() >= 1) {
          return (new Date(this.currentYear, this.currentMonth, 1).getDay() - 1);
        }
        return 6;
      }
    },

    countOfFirstDays() {
      if (new Date(this.currentYear, this.currentMonth + 1, 1).getDay() >= 1) {
        return (new Date(this.currentYear, this.currentMonth + 1, 1).getDay() - 1);
      }
      return 6;
    },

    LastDaysPrevMonth() {
      let arr = [];
      let prevMonthDays = null;
      if (this.currentMonth === 0) {
        prevMonthDays = 31;
      } else {
        prevMonthDays = this.$moment(`${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}`).daysInMonth();
      }

      for (let number = 1; number <= prevMonthDays; number++) {
        arr.push(number)
      }
      if (this.countOfLastDays() === 0) {
        return [];
      } else {
        return arr.slice(- this.countOfLastDays());
      }
    },

    FirstDaysNextMonth() {
      let arr = [];
      let nextMonthDays = null;
      if (this.currentMonth === 11) {
        nextMonthDays = 31;
      } else {
        nextMonthDays = this.$moment(`${this.currentYear}-${String(this.currentMonth + 2).padStart(2, '0')}`).daysInMonth();
      }

      for (let number = 1; number <= nextMonthDays; number++) {
        arr.push(number)
      }
      if (this.countOfFirstDays() === 0) {
        return [];
      } else {
        return arr.slice(0, (7 - this.countOfFirstDays()));
      }
    },

    daysInWeek() {
      let currentDate = this.$moment(`${this.currentYear}W${this.currentWeek}`);
      let weekStart = currentDate.clone().startOf('week');

      const days = [];
      for (let i = 0; i <= 6; i++) {
        days.push(this.$moment(weekStart).add(i, 'days').format("D"));
      };

      return days
    },

    goToToday() {
      this.currentMonth = this.$moment().month();
      this.currentYear = this.$moment().year();
      this.currentWeek = this.$moment(this.$moment().toDate(), "MM-DD-YYYY").isoWeek();
    },

    isToday(day) {
      return new Date().toLocaleDateString() === new Date(this.currentYear, this.currentMonth, day).toLocaleDateString();
    },

    lastWeekYear() {
      return Math.max(
          this.$moment(new Date(this.currentYear, 11, 31)).isoWeek(),
          this.$moment(new Date(this.currentYear, 11, 31-7)).isoWeek()
        )
    },

    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
        this.currentWeek = '01';
      } else {
        this.currentMonth++
        this.currentWeek = this.$moment(`${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}`).isoWeek();
      }
    },

    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
        this.currentWeek = this.$moment(`${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}`).isoWeek();
      } else {
        this.currentMonth--;
        this.currentWeek = this.$moment(`${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}`).isoWeek();
      }
    },

    prevWeek() {
      if (this.currentWeek === '01') {
        this.prevMonth();
      } else {
        this.currentWeek--;
        this.currentMonth = this.$moment(this.currentYear).add(this.currentWeek, 'weeks').month();
      }
    },

    nextWeek() {
      if (+this.currentWeek === this.lastWeekYear()) {
        this.nextMonth();
      } else {
        this.currentWeek++;
        this.currentMonth = this.$moment(this.currentYear).add(this.currentWeek, 'weeks').month();
      }
    },

    setDateToTask(currentDay, time, event) {
      this.currentDay = currentDay;
      this.time = time;
      this.taskData.realization_at = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(this.currentDay).padStart(2, '0')}T${this.time}`;

      if (event.target.closest('.calendar-main-time-block-task-wrapper')) {
        return;
      }

      this.openCreateTaskSidebar();
    },

    getTaskList() {
      let lastDay = this.$moment(`${this.currentYear}-${String(this.currentMonth + 2).padStart(2, '0')}`, 'YYYY-MM').daysInMonth();
      if (this.currentMonth === 11) {
        lastDay = this.$moment(`${this.currentYear + 1}-01`, 'YYYY-MM').daysInMonth();
      }
      let startDate = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-01`;
      if (this.currentMonth === 0) {
        startDate = `${this.currentYear - 1}-12-01`;
      }
      let endDate = `${this.currentYear}-${String(this.currentMonth + 2).padStart(2, '0')}-${lastDay}`;
      if (this.currentMonth + 2 === 13) {
        endDate = `${this.currentYear + 1}-01-${lastDay}`;
      }

      let user_id_params = '';
      if (this.calendarUserId) {
        user_id_params = `user_id=${this.calendarUserId}&`
      }
      let getParam = `?${user_id_params}from=${startDate}&to=${endDate}`;

      api.call('get', `${API_TASKS}/${getParam}`).then(({ data }) => {
        this.allTasks = data.data;
        this.isTaskRefreshing = false;
      });
    },

    refreshDataInTasks() {
      this.isTaskRefreshing = true;
      this.getTaskList();
    },

    filterTaskByDay(day) {
      let tasks = this.filtredTasksByTags.filter(task => {
        if (task.realization_at) {
          return task.reference_status_id != 403 && task.reference_status_id != 402 && task.realization_at.split(' ')[0] === `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        }
      })
      return tasks.slice(0, 4)
    },

    sumOfTaskByDay(day) {
      let tasks = this.filtredTasksByTags.filter(task => {
        if (task.realization_at) {
          return task.reference_status_id != 403 && task.reference_status_id != 402 && task.realization_at.split(' ')[0] === `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        }
      })
      return tasks.length;
    },

    openWeekWithDay(day, event) {
      if (event.target.closest('.calendar-main-time-block-task-wrapper')) {
        return
      }
      this.weekIsShow = true;
      this.currentWeek = this.$moment(this.$moment(`${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`)).isoWeek();
    },

    setRealizatorUserId(id) {
      this.selectedPerformers = id
    },

    setUserId(id) {
      this.calendarUserId = id
    },

    activeTag(tagName) {
      this.tags[tagName].active = !this.tags[tagName].active;
    },

    handlerStyleTags(task) {
      if (task.order_id) {
        return 'tags-style-order_id'
      }
      if (task.company_id) {
        return 'tags-style-company_id'
      }
      if (task.order_service_id) {
        return 'tags-style-order_service_id'
      }
      if (task.finance_id) {
        return 'tags-style-finance_id'
      }
      return 'tags-style-notification'
    },

    resetTaskData() {
      this.taskData = {
        order_id: '',
        company_id: '',
        finance_id: '',
        order_service_id: '',
        title: '',
        description: '',
        realization_at: '',
        reference_type_id: '406',
        realizators: [],
      }
    },

    changeTask(task) {
      this.openTaskSidebar(task);
    },

    moved(e) {
      if (e.added) {
        const movedTask = e.added.element;
        const month = e.currentDay.month;
        let year = e.currentDay.year;
        

        if (this.currentMonth === 0 && month === 12) {
          year--
        } else if (this.currentMonth === 11 && month === 1) {
          year++
        }

        movedTask.realization_at = `${year}-${String(month).padStart(2, '0')}-${String(e.currentDay.day).padStart(2, '0')} ${e.time.time}`;
        console.log(movedTask.realization_at);
        const index = this.allTasks.findIndex(t => t.id === movedTask.id);

        this.allTasks[index] = movedTask;

        this.allTasks = [...this.allTasks];
        
        api.call('put', `${API_TASKS}/${movedTask.id}`, movedTask)
          .then(() => this.refreshDataInTasks())
      }
    },
  },
}
</script>
